import { create } from "zustand";
// Asumsikan file apis/index.js mengekspor instance axios yang sudah dikonfigurasi
import { authenticatedAxios, baseURL } from "../../apis";

export const useExamsList = create((set, get) => ({
  /**
   * =================================
   * STATE
   * =================================
   */
  exams: [],
  exam: null,
  totalExams: 0,
  isLoading: false,
  error: null,

  /**
   * =================================
   * ACTIONS
   * =================================
   */

  /**
   * Mengambil daftar ujian berdasarkan ID kursus.
   * Endpoint: /api/admin/courses/<course_id>/exams/
   * @param {number} courseId - ID dari kursus.
   */
  fetchExams: async (courseId) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/courses/${courseId}/exams/`,
      );
      // Asumsi API mengembalikan format paginasi standar DRF
      set({
        exams: data.results || [],
        totalExams: data.count || 0,
        isLoading: false,
      });
      return data.results;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      set({ error: errorMessage, isLoading: false });
      console.error("Failed to fetch exams:", err);
      return null;
    }
  },

  /**
   * Mengambil detail satu ujian spesifik.
   * Endpoint: /api/admin/courses/exams/<exam_id>/
   * @param {number} examId - ID ujian.
   */
  fetchExamDetail: async (examId) => {
    set({ isLoading: true, error: null, exam: null });
    try {
      const { data } = await authenticatedAxios.get(
        `${baseURL}/api/admin/courses/exams/${examId}/`,
      );
      set({ exam: data, isLoading: false });
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      set({ error: errorMessage, isLoading: false });
      console.error("Failed to fetch exam detail:", err);
      return null;
    }
  },

  /**
   * Membuat ujian baru.
   * Endpoint: /api/admin/courses/exams/create/
   * @param {object} examData - Data ujian baru.
   */
  createExam: async (examData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await authenticatedAxios.post(
        `${baseURL}/api/admin/courses/exams/create/`,
        examData,
      );
      // Tambahkan ujian baru ke state yang sudah ada jika relevan
      if (
        examData.course &&
        examData.course.toString() === get().exams[0]?.course?.toString()
      ) {
        set((state) => ({
          exams: [...state.exams, data],
          totalExams: state.totalExams + 1,
        }));
      }
      set({ isLoading: false });
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      set({ error: errorMessage, isLoading: false });
      console.error("Failed to create exam:", err);
      throw err;
    }
  },

  /**
   * Mengupdate data ujian.
   * Endpoint: /api/admin/courses/exams/<exam_id>/update/
   * @param {number} examId - ID ujian.
   * @param {object} updateData - Data yang akan diubah.
   */
  updateExam: async (examId, updateData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await authenticatedAxios.put(
        `${baseURL}/api/admin/courses/exams/${examId}/update/`,
        updateData,
      );
      // Update daftar ujian di state
      set((state) => ({
        exams: state.exams.map((e) => (e.id === examId ? data : e)),
        exam: state.exam && state.exam.id === examId ? data : state.exam,
        isLoading: false,
      }));
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      set({ error: errorMessage, isLoading: false });
      console.error("Failed to update exam:", err);
      throw err;
    }
  },

  /**
   * Mengubah status publish sebuah ujian.
   * Endpoint: /api/admin/courses/exams/<exam_id>/toggle-status/
   * @param {number} examId - ID ujian.
   */
  toggleExamStatus: async (examId) => {
    // Optimistic UI update bisa ditambahkan di sini jika perlu
    try {
      const { data } = await authenticatedAxios.put(
        `${baseURL}/api/admin/courses/exams/${examId}/toggle-status/`,
        {},
      );
      set((state) => ({
        exams: state.exams.map((e) => (e.id === examId ? data : e)),
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      set({ error: errorMessage }); // Tidak set loading karena bisa berjalan di background
      console.error("Failed to toggle exam status:", err);
    }
  },

  /**
   * Menghapus ujian.
   * Endpoint: /api/admin/courses/exams/<exam_id>/delete/
   * @param {number} examId - ID ujian.
   */
  deleteExam: async (examId) => {
    try {
      await authenticatedAxios.delete(
        `${baseURL}/api/admin/courses/exams/${examId}/delete/`,
      );
      // Hapus ujian dari state
      set((state) => ({
        exams: state.exams.filter((e) => e.id !== examId),
        totalExams: state.totalExams - 1,
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      set({ error: errorMessage });
      console.error("Failed to delete exam:", err);
    }
  },

  /**
   * Membersihkan state ujian saat keluar dari halaman detail.
   */
  clearExamDetail: () => {
    set({ exam: null, error: null });
  },
}));
